import { css } from '@emotion/react';
import { Avatar, color, spacing, typography } from '@uniquegood/realworld-studio-design';

import { ChannelOption } from '../types';

interface ChannelSelectorItemProps {
  channel: ChannelOption;
  isSelected: boolean;
  onSelect: (channelId: string) => void;
}

/**
 * 채널 선택 아이템. 단일 채널이어도 명시적으로 selector 카드로 표시한다.
 *
 * 시각적으로는 `pages/checkout/selectChannel/components/ChannelItem.tsx` 와 비슷하나,
 * - 디자인 시스템 토큰만 사용
 * - radio 표시는 선택 상태에 맞춰 boolean으로 토글
 */
export default function ChannelSelectorItem({
  channel,
  isSelected,
  onSelect
}: ChannelSelectorItemProps) {
  const handleClick = () => {
    onSelect(channel.id);
  };

  return (
    <button
      type="button"
      css={[styles.container, isSelected && styles.selected]}
      onClick={handleClick}
      aria-pressed={isSelected}
      data-testid="connect-channel-item"
    >
      <Avatar size="medium" isCreator source={channel.imageUrl ?? undefined} name={channel.name} />
      <div css={styles.info}>
        <div css={styles.name}>{channel.name}</div>
        {channel.description ? <div css={styles.description}>{channel.description}</div> : null}
      </div>
      <span css={[styles.radio, isSelected && styles.radioSelected]} aria-hidden="true" />
    </button>
  );
}

const styles = {
  container: css`
    display: flex;
    align-items: center;
    gap: ${spacing.common.medium};
    width: 100%;
    padding: 10px ${spacing.common.medium};
    border-radius: ${spacing.borderRadius.medium};
    border: 1px solid ${color.border_default_subdued};
    background: ${color.surface_default_default};
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    text-align: left;

    &:hover {
      border-color: ${color.interactive_primary_default};
    }

    &:focus-visible {
      outline: 2px solid ${color.interactive_primary_default};
      outline-offset: 2px;
    }
  `,
  selected: css`
    border-color: ${color.interactive_primary_default};
    background: ${color.surface_default_subdued};
  `,
  info: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  `,
  name: css`
    color: ${color.text_default};
    font-size: ${typography.size.s2};
    line-height: 1.4;
    font-weight: ${typography.weight.bold};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  description: css`
    color: ${color.text_subdued};
    font-size: ${typography.size.s1};
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  radio: css`
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1.5px solid ${color.border_default_subdued};
    background: transparent;
    position: relative;
  `,
  radioSelected: css`
    border-color: ${color.interactive_primary_default};
    background: ${color.interactive_primary_default};

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #ffffff;
    }
  `
};
